'use client';

import { useSyncExternalStore } from 'react';
import { CircleHelp, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { subscribe, getSnapshot, resolveConfirm } from '@/lib/confirm';

/**
 * Single global confirmation dialog. Mounted once in Providers; driven by the
 * src/lib/confirm store so the axios interceptor (and any component) can prompt
 * the user before modifying existing data.
 *
 * Built on the shadcn/Radix Dialog so it stacks correctly over other Radix
 * dialogs (e.g. LeadFormModal). Radix coordinates layered dialogs via a global
 * DismissableLayer stack, so the top-most layer (this confirm) stays interactive
 * even when it's raised from inside another open dialog.
 */
export default function ConfirmRoot() {
  const pending = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const o = pending?.opts || {};
  const destructive = !!o.destructive;
  const Icon = destructive ? TriangleAlert : CircleHelp;
  return (
    <Dialog open={!!pending} onOpenChange={(open) => { if (!open) resolveConfirm(false); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={cn('h-5 w-5 shrink-0', destructive ? 'text-destructive' : 'text-primary')} />
            {o.title || 'Confirm change'}
          </DialogTitle>
          <DialogDescription>
            {o.message || 'Do you want to save these changes to the existing record?'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => resolveConfirm(false)}>
            {o.cancelText || 'Cancel'}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => resolveConfirm(true)}
            autoFocus
          >
            {o.confirmText || 'Yes, save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
